import { useEffect, useRef, useState } from 'react';
import { authService } from '@services/auth.service';

/**
 * Page de connexion pour les opérateurs
 * Authentification via Microsoft OAuth
 */
export const LoginPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Ajuster la hauteur du widget Crisp
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current && (window as any).$crisp?.setHeight) {
        const height = containerRef.current.scrollHeight;
        const crispHeight = Math.min(height + 20, 600); // +20 pour le padding
        console.log('Setting Crisp widget height to:', crispHeight);
        (window as any).$crisp.setHeight(crispHeight);
      }
    };

    const timer = setTimeout(updateHeight, 100);
    return () => clearTimeout(timer);
  }, [isLoading]);

  useEffect(() => {
    console.log('🎧 Setting up auth token polling...');
    console.log('📍 Window location:', window.location.href);
    console.log('🌐 Window origin:', window.location.origin);
    console.log('🪟 Window context:', {
      isInIframe: window.self !== window.top,
      hasOpener: !!window.opener,
    });

    // Afficher TOUT le contenu de localStorage
    console.log('🗄️ Full localStorage content:');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key);
        console.log(`  - ${key}:`, value?.substring(0, 50) + (value && value.length > 50 ? '...' : ''));
      }
    }

    console.log('🔍 Initial localStorage check:', {
      token: localStorage.getItem('ms_auth_token') ? 'EXISTS' : 'EMPTY',
      error: localStorage.getItem('ms_auth_error') ? 'EXISTS' : 'EMPTY',
    });

    console.log('📨 Setting up postMessage listener for auth...');

    // Écouter les messages de l'onglet d'auth
    const handleMessage = (event: MessageEvent) => {
      console.log('📬 postMessage received:', {
        origin: event.origin,
        data: event.data,
        source: event.source ? 'has source' : 'no source',
      });

      // Vérifier que c'est bien un message de notre auth
      if (event.data?.source === 'crisp-widget-auth') {
        if (event.data.type === 'MS_AUTH_SUCCESS' && event.data.token) {
          console.log('✅ Auth token received via postMessage!');
          console.log('🔑 Token preview:', event.data.token.substring(0, 20) + '...');

          // Stocker le JWT
          authService.setToken(event.data.token);

          // Recharger pour initialiser le widget
          console.log('🔄 Reloading page...');
          window.location.reload();
        } else if (event.data.type === 'MS_AUTH_ERROR') {
          console.log('❌ Auth error via postMessage:', event.data.error);
          setIsLoading(false);
          alert('Erreur lors de l\'authentification. Veuillez réessayer.');
        }
      } else {
        console.log('ℹ️ Ignored message (not from crisp-widget-auth)');
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('✅ postMessage listener ready');

    return () => {
      console.log('🔇 Removing postMessage listener');
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleMicrosoftLogin = () => {
    setIsLoading(true);

    console.log('🔐 Opening Microsoft login in new tab...');

    // Ouvrir dans un nouvel onglet
    const authWindow = window.open(
      'https://login.paladium.games/microsoft/link?redirect_type=crisp-widget',
      '_blank'
    );

    if (!authWindow) {
      console.error('❌ Failed to open auth window - blocked by browser');
      alert('Impossible d\'ouvrir la fenêtre de connexion. Vérifiez que les popups ne sont pas bloquées.');
      setIsLoading(false);
      return;
    }

    console.log('✅ Auth window opened');
    console.log('📍 Widget origin:', window.location.origin);
    console.log('⏳ Waiting for authentication to complete...');

    // Timeout après 5 minutes
    const timeout = setTimeout(() => {
      console.log('⏱️ Auth timeout');
      setIsLoading(false);
      alert('Délai d\'attente dépassé. Veuillez réessayer.');
    }, 300000);

    // Nettoyer le timeout si le composant est démonté
    return () => clearTimeout(timeout);
  };

  return (
    <div ref={containerRef} className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-6">
      <div className="max-w-md w-full mx-auto bg-white rounded-2xl shadow-xl p-8">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-4xl">🎮</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Paladium Widget
          </h1>
          <p className="text-gray-600">
            Connectez-vous pour accéder au support
          </p>
        </div>

        {/* Login Button */}
        <button
          onClick={handleMicrosoftLogin}
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              <span>Connexion en cours...</span>
            </>
          ) : (
            <>
              <svg
                className="w-6 h-6 group-hover:scale-110 transition-transform"
                viewBox="0 0 23 23"
                fill="none"
              >
                <path d="M11 11V0H0v11h11z" fill="#f25022"/>
                <path d="M23 11V0H12v11h11z" fill="#00a4ef"/>
                <path d="M11 23V12H0v11h11z" fill="#7fba00"/>
                <path d="M23 23V12H12v11h11z" fill="#ffb900"/>
              </svg>
              <span>Se connecter avec Microsoft</span>
            </>
          )}
        </button>

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-sm text-blue-900 text-center">
            🔒 Authentification sécurisée via votre compte Microsoft
          </p>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            Vous devez être un opérateur autorisé pour accéder à cet outil.
          </p>
        </div>
      </div>
    </div>
  );
};
